import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const FilterPanel = ({ filters, onFilterChange, onClearFilters, resultCount }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const categories = [
    { value: 'all', label: 'All Templates', count: 48 },
    { value: 'business', label: 'Business', count: 12 },
    { value: 'portfolio', label: 'Portfolio', count: 8 },
    { value: 'ecommerce', label: 'E-Commerce', count: 10 },
    { value: 'landing', label: 'Landing Pages', count: 15 },
    { value: 'blog', label: 'Blog', count: 3 }
  ];

  const industries = [
    { value: 'all', label: 'All Industries' },
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Finance' },
    { value: 'education', label: 'Education' },
    { value: 'retail', label: 'Retail' },
    { value: 'realestate', label: 'Real Estate' }
  ];

  const featureTags = [
    { id: 'responsive', label: 'Responsive', icon: 'Smartphone' },
    { id: 'dark-mode', label: 'Dark Mode', icon: 'Moon' },
    { id: 'animations', label: 'Animations', icon: 'Sparkles' },
    { id: 'forms', label: 'Forms', icon: 'FileText' },
    { id: 'ecommerce', label: 'E-Commerce', icon: 'ShoppingCart' },
    { id: 'blog', label: 'Blog', icon: 'BookOpen' }
  ];

  const sortOptions = [
    { value: 'popular', label: 'Most Popular' },
    { value: 'newest', label: 'Newest First' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'downloads', label: 'Most Downloaded' }
  ];

  const handleCategoryClick = (categoryValue) => {
    onFilterChange({ ...filters, category: categoryValue });
  };

  const handleFeatureToggle = (featureId) => {
    const currentFeatures = filters?.features || [];
    const newFeatures = currentFeatures?.includes(featureId)
      ? currentFeatures?.filter(f => f !== featureId)
      : [...currentFeatures, featureId];
    onFilterChange({ ...filters, features: newFeatures });
  };

  const activeFiltersCount = 
    (filters?.category !== 'all' ? 1 : 0) +
    (filters?.industry !== 'all' ? 1 : 0) +
    (filters?.features?.length || 0);

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground">
            Filters
          </h2>
          {activeFiltersCount > 0 && (
            <span className="px-2.5 py-1 bg-primary text-primary-foreground rounded-full text-xs font-medium">
              {activeFiltersCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeFiltersCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              iconPosition="left"
              onClick={onClearFilters}
            >
              Clear
            </Button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="lg:hidden p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-250"
            aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
          >
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={20} />
          </button>
        </div>
      </div>
      <div className={`space-y-6 ${isExpanded ? 'block' : 'hidden lg:block'}`}>
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="caption font-medium text-foreground">Categories</h3>
            <span className="text-xs text-muted-foreground">{resultCount} results</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories?.map((category) => {
              const isActive = filters?.category === category?.value;
              return (
                <button
                  key={category?.value}
                  onClick={() => handleCategoryClick(category?.value)}
                  className={`px-3 md:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-250 ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                  }`}
                >
                  {category?.label}
                  <span className="ml-1.5 opacity-70">({category?.count})</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="caption font-medium text-foreground mb-3">Industry</h3>
          <Select
            options={industries}
            value={filters?.industry || 'all'}
            onChange={(value) => onFilterChange({ ...filters, industry: value })}
            placeholder="Select industry"
          />
        </div>

        <div>
          <h3 className="caption font-medium text-foreground mb-3">Features</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {featureTags?.map((tag) => {
              const isActive = filters?.features?.includes(tag?.id);
              return (
                <button
                  key={tag?.id}
                  onClick={() => handleFeatureToggle(tag?.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-250 ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                  }`}
                >
                  <Icon name={tag?.icon} size={16} />
                  <span className="truncate">{tag?.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="caption font-medium text-foreground mb-3">Sort By</h3>
          <Select
            options={sortOptions}
            value={filters?.sortBy || 'popular'}
            onChange={(value) => onFilterChange({ ...filters, sortBy: value })}
            placeholder="Sort templates"
          />
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;